import { Button, CircularProgress, IconButton, OutlinedInput, Stack, Typography, useTheme } from "@mui/material";
import React, { useCallback, useState } from "react";
import { assetService } from "../../../../Core/Api/services";
import { useTranslation } from "react-i18next";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { InfoOutlined, SearchOutlined } from "@mui/icons-material";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import SearchIcon from "@mui/icons-material/Search";
import AssetPanel from "../../../../Components/Panels/AssetPanel/asset-panel";
import { generateAssetNameFromNr, TokenizedNamingRule } from "./commons";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import SettingsIcon from "@mui/icons-material/Settings";
interface ExampleNamesListProps {
  tokenizedNamingRule: TokenizedNamingRule;
  metadataFields: DbMetadataFieldInfos[];
  handleConfigClick: (e: any) => void;
}
const ExampleNamesList: React.FC<ExampleNamesListProps> = ({
  tokenizedNamingRule,
  metadataFields,
  handleConfigClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [assets, setAssets] = useState<DbAssetObjectInfos[]>([]);
  const [searchId, setSearchId] = useState<undefined | number>(undefined);
  const [loading, setLoading] = useState(false);
  const [noResult, setNoResult] = useState(false);
  const [panelAssetId, setPanelAssetId] = useState<undefined | number>();

  const handleSearchById = useCallback(() => {
    if (!searchId) return;
    setLoading(true);
    setNoResult(false);
    setAssets([]);
    assetService.GetObjectById(searchId).then((response) => {
      if (response.success && response.graph) setAssets([response.graph]);
      else {
        setNoResult(true);
      }
      setLoading(false);
    });
  }, [searchId]);

  return (
    <Stack
      border={`1px solid ${theme.palette.background[300]}`}
      borderRadius={"12px"}
      position={"relative"}
      top={"-8px"}
      py={1}
      px={0}
      mx={1}
      sx={{ background: theme.palette.background.alphaHover }}
      spacing={1}
      direction={"column"}
    >
      <Stack px={1} direction={"row"} justifyContent={"space-between"}>
        <Stack px={1} alignItems={"center"} direction={"row"} spacing={2}>
          <Typography variant="h6" color={"text.secondary"}>
            {t("form:section.title.searchAssetId")}
          </Typography>
          <Stack alignItems={"center"} direction={"row"} spacing={1}>
            <OutlinedInput
              onChange={(e) => setSearchId(parseInt(e.target.value))}
              value={searchId}
              placeholder="Asset id"
              type="number"
            />
            <Button disabled={!searchId || !(searchId > 0)} onClick={handleSearchById} variant="outlined">
              <SearchOutlined color="primary" />
            </Button>
            {assets.length > 0 && (
              <IconButton disabled={assets.length < 1} onClick={() => setPanelAssetId(assets[0].id)} size="small">
                <InfoOutlined color="info" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Button onClick={handleConfigClick} variant="outlined" color="primary" endIcon={<SettingsIcon />}>
          {t("button:generateAssetsNames")}
        </Button>
      </Stack>
      {!loading && assets.length > 0 && (
        <Stack
          sx={{ background: theme.palette.background.default, border: `1px solid grey` }}
          p={1}
          borderRadius={1}
          spacing={1}
          direction={"column"}
          mx={"16px !important"}
        >
          {assets.map((x, i) => (
            <Stack mx={2} width={"100%"} spacing={2} direction={"column"} alignItems={"center"}>
              <Stack width={"100%"} direction={"row"} justifyContent={"space-between"}>
                <Typography variant="h6" color={"text.tier"}>
                  {t("common:generatedName")}
                </Typography>
                <Typography variant="body1" fontSize={"12px"}>
                  {generateAssetNameFromNr(x, tokenizedNamingRule, metadataFields)}
                </Typography>
              </Stack>
              <Stack width={"100%"} direction={"row"} justifyContent={"space-between"}>
                <Typography variant="h6" color={"text.tier"}>
                  {t("common:currentName")}
                </Typography>
                <Typography color={"text.secondary"} variant="body1" fontSize={"12px"}>
                  {x.name}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      {assets.length === 0 && (
        <Stack
          sx={{ background: theme.palette.background.default, border: `1px solid grey` }}
          p={2}
          mx={"16px !important"}
          justifyContent={"center"}
          alignItems={"center"}
          borderRadius={1}
        >
          {loading ? (
            <CircularProgress />
          ) : noResult ? (
            <Stack alignItems={"center"} direction={"column"} spacing={2}>
              <Typography color={"text.tier"}>{t("modal:body.noResultsFound")}</Typography>
              <NotInterestedIcon color="error" />
            </Stack>
          ) : (
            <Stack alignItems={"center"} direction={"column"} spacing={2}>
              <Typography color={"text.tier"}>{t("form:section.title.searchAssetId")}</Typography>
              <SearchIcon />
            </Stack>
          )}
        </Stack>
      )}
      <AssetPanel
        open={panelAssetId !== undefined}
        assetId={panelAssetId}
        onClose={() => {
          setPanelAssetId(undefined);
        }}
        closePanel={() => {
          setPanelAssetId(undefined);
        }}
      />
    </Stack>
  );
};

export default ExampleNamesList;
